import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { TABLE_CELL, TABLE_HEADER } from '@/components/ui/pageChrome';
import { UI_MUTED } from '@/lib/typography';
import { SectionCard } from './ClinicGeneral';
import { useServicesList } from '@/hooks/useServices';
import {
  useCompensationRules,
  useDeleteCompensationRule,
  useSaveCompensationRule,
} from '@/hooks/usePractitionerReports';
import { format } from 'date-fns';

interface PractitionerCompensationProps {
  clinicId: string;
  practitionerId: string;
  canEdit: boolean;
}

export function PractitionerCompensation({
  clinicId,
  practitionerId,
  canEdit,
}: PractitionerCompensationProps) {
  const { t } = useTranslation();
  const { data: rules, isLoading, error } = useCompensationRules(clinicId, practitionerId);
  const { data: services } = useServicesList(clinicId, { includeInactive: true });
  const saveRule = useSaveCompensationRule(clinicId);
  const deleteRule = useDeleteCompensationRule(clinicId);

  const [percent, setPercent] = useState('50');
  const [serviceId, setServiceId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const serviceName = useMemo(() => {
    const map = new Map((services ?? []).map((service) => [service.id, service.name]));
    return (id: string | null) => (id ? (map.get(id) ?? id) : t('users.compensation.default_rate'));
  }, [services, t]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const value = Number(percent.replace(',', '.'));
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      toast.error(t('users.compensation.invalid_percent'));
      return;
    }
    try {
      await saveRule.mutateAsync({
        practitionerId,
        serviceId: serviceId || null,
        commissionPercent: value,
        effectiveFrom,
        closeExisting: true,
      });
      toast.success(t('users.compensation.saved'));
    } catch {
      toast.error(t('users.compensation.save_error'));
    }
  }

  return (
    <SectionCard
      title={t('users.section_compensation')}
      subtitle={t('users.compensation.subtitle')}
      readOnly={!canEdit}
    >
      <p className="mb-4 text-sm text-ds-muted">
        {t('users.compensation.history_hint')}
      </p>

      {canEdit && (
        <form onSubmit={onSubmit} className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <label className="text-xs font-medium text-ds-text">
            {t('users.compensation.service')}
            <select
              value={serviceId}
              onChange={(event) => setServiceId(event.target.value)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
            >
              <option value="">{t('users.compensation.default_rate')}</option>
              {(services ?? [])
                .filter((service) => !service.is_blocker)
                .map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="text-xs font-medium text-ds-text">
            {t('users.compensation.percent')}
            <input
              type="text"
              value={percent}
              onChange={(event) => setPercent(event.target.value)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
            />
          </label>
          <label className="text-xs font-medium text-ds-text">
            {t('users.compensation.effective_from')}
            <input
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
              className="mt-1 block h-[40px] w-full rounded-xl border border-ds-field bg-ds-card px-2 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
              required
            />
          </label>
          <div className="flex items-end">
            <Button type="submit" size="sm" disabled={saveRule.isPending} className="w-full">
              <Plus className="mr-1 h-4 w-4" />
              {t('users.compensation.add_rule')}
            </Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
        </div>
      ) : error ? (
        <p className="text-sm text-ds-red-ink">{t('users.compensation.load_error')}</p>
      ) : !rules || rules.length === 0 ? (
        <div className={`rounded-md border border-dashed border-ds-line px-4 py-8 text-center ${UI_MUTED}`}>
          {t('users.compensation.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left">
            <thead className={`uppercase tracking-wide ${TABLE_HEADER}`}>
              <tr>
                <th className="py-2 pr-3">{t('users.compensation.service')}</th>
                <th className="py-2 pr-3">{t('users.compensation.percent')}</th>
                <th className="py-2 pr-3">{t('users.compensation.effective_from')}</th>
                <th className="py-2 pr-3">{t('users.compensation.effective_to')}</th>
                {canEdit && <th className="py-2" />}
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-t border-ds-line">
                  <td className={`py-2 pr-3 ${TABLE_CELL}`}>
                    {serviceName(rule.service_id)}
                  </td>
                  <td className="py-2 pr-3">
                    {Number(rule.commission_percent).toLocaleString('da-DK')}%
                  </td>
                  <td className="py-2 pr-3">{rule.effective_from}</td>
                  <td className="py-2 pr-3">
                    {rule.effective_to ?? t('users.compensation.open_ended')}
                  </td>
                  {canEdit && (
                    <td className="py-2 text-right">
                      <Button
                        type="button"
                        variant="danger"
                        size="icon"
                        onClick={async () => {
                          if (!window.confirm(t('users.compensation.confirm_delete'))) return;
                          try {
                            await deleteRule.mutateAsync({ id: rule.id, practitionerId });
                            toast.success(t('users.compensation.deleted'));
                          } catch {
                            toast.error(t('users.compensation.save_error'));
                          }
                        }}
                        aria-label={t('common.delete')}
                      >
                        <Trash2 />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
